import React, { useState } from 'react';
import { couponService } from '../../services/couponService';
import BaseModal from '../common/BaseModal';

function SimpleUseCouponModal({ coupon, isOpen, onClose, onSuccess }) {
  const [receiptNumber, setReceiptNumber] = useState('');
  const [amountUsed, setAmountUsed] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!coupon) return null;

  // Saldo kuponu (model saldowy: wartosc = to, co zostalo do wykorzystania)
  const saldo = parseFloat(coupon.wartosc) || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Powod uzycia jest OBOWIAZKOWY: reczne zdjecie kuponu bez sladu
    // (numeru paragonu / powodu) bylo zrodlem kuponow "wykorzystanych"
    // bez pokrycia. Przycisk w stopce omija walidacje formularza,
    // wiec sprawdzamy tutaj.
    if (!receiptNumber || receiptNumber.trim() === '') {
      setError('Podaj numer paragonu albo powod uzycia - bez tego kupon nie zostanie zdjety.');
      return;
    }

    // Kwota: domyslnie cale saldo; czesciowe uzycie zostawia kupon aktywny
    // z reszta salda (serwer pilnuje, zeby nie zdjac wiecej niz saldo).
    let kwota = saldo;
    if (amountUsed !== '' && amountUsed !== null) {
      kwota = Math.round((parseFloat(amountUsed) || 0) * 100) / 100;
      if (!(kwota > 0)) {
        setError('Kwota do wykorzystania musi byc wieksza od zera.');
        return;
      }
      if (kwota > saldo) {
        setError(`Kwota ${kwota.toFixed(2)} zl przekracza saldo kuponu (${saldo.toFixed(2)} zl).`);
        return;
      }
    }

    setLoading(true);
    try {
      // Wspolna, atomowa konsumpcja serwera (K1): blokada wiersza kuponu,
      // saldo maleje, wpis do historii - ta sama droga co finalizacja POS.
      await couponService.useCoupon(coupon.id, {
        receipt_number: receiptNumber.trim(),
        amount_used: kwota
      });

      onSuccess && onSuccess();
      onClose();
      setReceiptNumber('');
      setAmountUsed('');
    } catch (err) {
      console.error('❌ Błąd wykorzystywania kuponu:', err);
      // error_response serwera niesie komunikat w polu 'error' (nie 'message')
      let opis = err.response?.data?.error
        || err.response?.data?.message
        || 'Blad podczas wykorzystywania kuponu';
      if (typeof opis === 'string' && opis.indexOf('wykorzystany') !== -1) {
        opis += ' - kupon zostal juz zuzyty, byc moze przed chwila na innej kasie. Odswiez liste i sprawdz saldo.';
      }
      setError(opis);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Wykorzystaj kupon"
      size="sm"
      zIndex={1000}
      headerIcon={
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>
      }
      headerStyle={{
        border: '1px solid var(--info)',
        color: 'var(--info)',
      }}
      footer={
        <>
          <button
            type="button"
            onClick={() => { setReceiptNumber(''); setAmountUsed(''); setError(''); onClose(); }}
            style={{
              padding: '6px 12px',
              background: 'var(--bg)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              fontWeight: 500,
              fontSize: '12px',
            }}
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '6px 12px',
              border: `1px solid ${loading ? 'var(--info-light)' : 'var(--info)'}`,
              color: 'var(--info)',
              fontWeight: 600,
              fontSize: '12px',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Wykorzystywanie...' : 'Wykorzystaj'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} autoComplete="off">
        <div style={{ fontWeight: 500, fontSize: 15, color: 'var(--text)', marginBottom: 6 }}>Kod kuponu: <span style={{ fontWeight: 600 }}>{coupon.kod}</span></div>
        <div style={{ fontWeight: 500, fontSize: 15, color: 'var(--text)', marginBottom: 14 }}>Saldo do wykorzystania: <span style={{ fontWeight: 600 }}>{saldo.toFixed(2)} zł</span></div>
        <label htmlFor="receiptNumber" style={{ fontWeight: 500, fontSize: 15, color: 'var(--text)', marginBottom: 6, display: 'block' }}>Numer paragonu / powód użycia (wymagane)</label>
        <input
          id="receiptNumber"
          type="text"
          value={receiptNumber}
          onChange={e => { setReceiptNumber(e.target.value); setError(''); }}
          placeholder="Np. PAR/0123/09/2026 albo powód"
          style={{
            width: '100%',
            padding: '12px 14px',
            margin: '8px 0 14px 0',
            border: '1.5px solid var(--border)',
            borderRadius: 7,
            fontSize: 17,
            outline: 'none',
            boxSizing: 'border-box',
            background: 'var(--bg)',
            transition: 'border 0.2s',
            fontWeight: 500
          }}
          required
        />
        <label htmlFor="amountUsed" style={{ fontWeight: 500, fontSize: 15, color: 'var(--text)', marginBottom: 6, display: 'block' }}>Kwota do wykorzystania (puste = całe saldo)</label>
        <input
          id="amountUsed"
          type="number"
          min="0.01"
          step="0.01"
          max={saldo}
          value={amountUsed}
          onChange={e => { setAmountUsed(e.target.value); setError(''); }}
          placeholder={saldo.toFixed(2)}
          style={{
            width: '100%',
            padding: '12px 14px',
            margin: '8px 0 18px 0',
            border: '1.5px solid var(--border)',
            borderRadius: 7,
            fontSize: 17,
            outline: 'none',
            boxSizing: 'border-box',
            background: 'var(--bg)',
            transition: 'border 0.2s',
            fontWeight: 500
          }}
        />
        {error && <div style={{ color: 'var(--danger)', background: 'var(--danger-light)', borderRadius: 5, padding: '8px 12px', marginBottom: 14, fontSize: 15 }}>{error}</div>}
      </form>
    </BaseModal>
  );
}

export default SimpleUseCouponModal;
